import { AlertCircle, AlertTriangle, FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import type { SubmissionWarning, WarningSeverity } from '@/lib/submissionWarnings';

interface WarningRowProps {
  warning: SubmissionWarning;
  severity: WarningSeverity;
  onPreviewDocument: (documentId: string) => void;
}

function WarningRow({ warning, severity, onPreviewDocument }: WarningRowProps) {
  const { t } = useTranslation();
  const isError = severity === 'error';
  const { documentId } = warning;

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-2 text-xs rounded px-2 py-1.5',
        isError ? 'text-red-800 bg-red-100/50' : 'text-amber-800 bg-amber-100/50',
      )}>
      <span>{warning.message}</span>
      {documentId && (
        <button
          type='button'
          onClick={() => onPreviewDocument(documentId)}
          className={cn(
            'inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium flex-shrink-0',
            isError
              ? 'bg-red-200/50 text-red-700 hover:bg-red-200'
              : 'bg-amber-200/50 text-amber-700 hover:bg-amber-200',
          )}>
          <FileText className='h-3 w-3' />
          {t('warnings.previewDocument')}
        </button>
      )}
    </div>
  );
}

interface WarningsBannerProps {
  warnings: SubmissionWarning[];
  onPreviewDocument: (documentId: string) => void;
}

export function WarningsBanner({ warnings, onPreviewDocument }: WarningsBannerProps) {
  const { t } = useTranslation();

  if (warnings.length === 0) {
    return null;
  }

  const errors = warnings.filter((w) => w.severity === 'error');
  const warningItems = warnings.filter((w) => w.severity === 'warning');

  return (
    <div className='rounded-lg border overflow-hidden'>
      {errors.length > 0 && (
        <div className='bg-red-50'>
          <div className='px-4 py-2 flex items-center gap-2 text-sm font-medium text-red-800'>
            <AlertCircle className='h-4 w-4' />
            {t('warnings.summaryErrors', { count: errors.length })}
          </div>
          <div className='px-4 pb-3 space-y-1'>
            {errors.map((warning, index) => (
              <WarningRow
                key={`error-${warning.field}-${index}`}
                warning={warning}
                severity='error'
                onPreviewDocument={onPreviewDocument}
              />
            ))}
          </div>
        </div>
      )}
      {warningItems.length > 0 && (
        <div className='bg-amber-50'>
          <div className='px-4 py-2 flex items-center gap-2 text-sm font-medium text-amber-800'>
            <AlertTriangle className='h-4 w-4' />
            {t('warnings.summaryWarnings', { count: warningItems.length })}
          </div>
          <div className='px-4 pb-3 space-y-1'>
            {warningItems.map((warning, index) => (
              <WarningRow
                key={`warning-${warning.field}-${index}`}
                warning={warning}
                severity='warning'
                onPreviewDocument={onPreviewDocument}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
